import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Wand2 } from 'lucide-react';
import { api } from '../api/client';
import { ErrorBox, errText, Modal, RiskBadge, Seg, useToast } from './ui';
import { fmtDateTime, fmtHours, partOf, plural } from '../lib/format';

interface Preview {
  proposals: { operationId: string; resourceId: string; resource: string; operation: string; jobReference: string; productCode?: string | null; start: string; end: string; hours: number; reasons: string[] }[];
  removed: { id: string; jobReference: string; operation: string; resource: string; start: string; end: string; hours: number }[];
  jobs: { jobId: string; reference: string; productCode?: string | null; projectedFinish: string | null; risk: string; riskReason: string; issues: { code: string; message: string }[] }[];
}

export function AutoScheduleDialog({ onClose, onApplied, jobIds }: { onClose: () => void; onApplied: () => void; jobIds?: string[] }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'unscheduled_only' | 'replan'>('unscheduled_only');
  const preview = useMutation({ mutationFn: () => api.post<Preview>('/api/schedule/preview', { mode, jobIds }) });
  const apply = useMutation({
    mutationFn: (p: Preview) => {
      const left: string[] = [];
      return api.post<{ created: number; removed: number }>('/api/schedule/apply', { proposals: p.proposals, removedAssignmentIds: p.removed.map((r) => r.id) }, {
        // Asked for a reason: the jobs listed are left as they are and the rest worked out again without them
        skip: async (refs) => {
          left.push(...refs.filter((r) => !left.includes(r)));
          const again = await api.post<Preview>('/api/schedule/preview', { mode, jobIds, exclude: left });
          return again.proposals.length ? { proposals: again.proposals, removedAssignmentIds: again.removed.map((r) => r.id) } : null;
        },
      });
    },
    onSuccess: (r) => { toast('ok', `${r.created} booked${r.removed ? ` · ${r.removed} moved` : ''}`); qc.invalidateQueries({ queryKey: ['schedule-undo'] }); onApplied(); onClose(); },
    onError: (e) => toast('bad', errText(e)),
  });
  const p = preview.data;
  const issues = p?.jobs.filter((j) => j.issues.length || j.risk !== 'on_schedule') ?? [];
  return (
    <Modal wide title={<span className="row"><Wand2 size={18} /> Auto-schedule{jobIds?.length ? ' (selected job)' : ''}</span>} onClose={onClose} footer={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      {!p ? <button className="btn primary" onClick={() => preview.mutate()} disabled={preview.isPending}>{preview.isPending ? 'Calculating…' : 'Preview'}</button>
        : <><button className="btn" onClick={() => preview.mutate()}>Recalculate</button><button className="btn primary" disabled={!p.proposals.length || apply.isPending} onClick={() => apply.mutate(p)} data-testid="apply-schedule">{p.proposals.length ? `Apply ${plural(p.proposals.length, 'booking')}` : 'Nothing to place'}</button></>}
    </>}>
      <div className="grid-2">
        <label className="field">Scope
          <Seg value={mode} onChange={(v) => { setMode(v); preview.reset(); }} options={[{ id: 'unscheduled_only', label: 'Unscheduled work only' }, { id: 'replan', label: 'Re-plan auto-scheduled work' }]} />
        </label>
      </div>
      {preview.error && <ErrorBox error={preview.error} />}
      {p && (
        <>
          {p.removed.length > 0 && <div className="alert warn">{plural(p.removed.length, 'booking')} will be moved</div>}
          {issues.length > 0 && (
            <div className="card">
              <div className="card-head"><h3 style={{ margin: 0 }}>Risk after scheduling</h3></div>
              <table className="tbl"><tbody>
                {issues.map((j) => <tr key={j.jobId}><td className="nowrap" title={j.reference}><b>{partOf(j)}</b></td><td><RiskBadge risk={j.risk} /></td><td>{j.riskReason}{j.issues.length > 1 && <div className="faint">{j.issues.slice(1).map((i) => i.message).join(' · ')}</div>}</td></tr>)}
              </tbody></table>
            </div>
          )}
          <div className="card table-wrap">
            <table className="tbl">
              <thead><tr><th>Part number</th><th>Operation</th><th>Person</th><th>Start</th><th>Finish</th><th className="num">Hours</th><th>Why</th></tr></thead>
              <tbody>
                {p.proposals.map((x, i) => (
                  <tr key={i}><td className="nowrap" title={x.jobReference}>{x.productCode || x.jobReference}</td><td>{x.operation}</td><td>{x.resource}</td><td className="nowrap">{fmtDateTime(x.start)}</td><td className="nowrap">{fmtDateTime(x.end)}</td><td className="num">{fmtHours(x.hours)}</td>
                    <td className="faint" style={{ fontSize: 12 }}>{x.reasons.slice(0, 3).join(' · ')}</td></tr>
                ))}
                {!p.proposals.length && <tr><td colSpan={7} className="empty">Nothing to place.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
